import React, { forwardRef, memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import CameraFeed from './camera-feed.js';
import { CctvOverlay, MotionBox } from './cctv-overlay.js';
import { FullscreenIcon, VideocamIcon } from './icons.js';
import { hms } from './media.js';

const EASE = [0.16, 1, 0.3, 1];

/**
 * Everything drawn inside one camera's frame, shared by the wall tile
 * and the full-screen viewer: the feed (or the no-signal card), the
 * motion box, the on-screen display and the snapshot flash.
 *
 * `status` is live, offline, reconnecting, or gap (playback inside a
 * stretch the camera was down for). It is a fragment, so whatever
 * frames it decides the shape and the stacking.
 */
export const CameraView = forwardRef(function CameraView({
    camera,
    status = 'live',
    since,
    onReconnect,
    playback = null,
    motion: box = null,
    talking = false,
    flashKey = 0,
    variant = 'full',
    active = true,
    priority = 0,
    zoom,
    pan,
    smooth,
    top = null
}, feedRef) {
    const live = status === 'live';

    return (
        <>
            {live ? (
                <CameraFeed
                    ref={feedRef}
                    camera={camera}
                    active={active}
                    priority={priority}
                    playback={playback}
                    zoom={zoom}
                    pan={pan}
                    smooth={smooth}
                />
            ) : (
                <NoSignal camera={camera} status={status} since={since} onReconnect={onReconnect} />
            )}
            {live && <MotionBox motion={box} />}
            <CctvOverlay
                camera={camera}
                status={status}
                playback={playback}
                variant={variant}
                talking={talking}
                top={top}
            />
            {flashKey > 0 && <span key={flashKey} className="cam-flash" aria-hidden="true" />}
        </>
    );
});

/* A camera that has stopped sending: what it last saw, and the way to
   bring it back. Reconnecting shows a spinner until the stream returns. */
function NoSignal({ camera, status, since, onReconnect }) {
    const busy = status === 'reconnecting';
    const gap = status === 'gap';

    return (
        <div className="cam-nosignal">
            <span className="cam-nosignal__noise" aria-hidden="true" />
            <div className="cam-nosignal__body">
                {busy
                    ? <span className="cam-spinner" aria-hidden="true" />
                    : <VideocamIcon className="cam-nosignal__icon" aria-hidden="true" />}
                <strong>{gap ? 'No recording' : busy ? 'Reconnecting' : 'Signal lost'}</strong>
                <span className="cam-nosignal__meta">
                    {gap ? 'The camera was offline at this time' : since ? `Last frame ${hms(since)}` : 'Not responding'}
                </span>
                {status === 'offline' && onReconnect && (
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm cam-nosignal__retry"
                        onClick={event => { event.stopPropagation(); onReconnect(camera.id); }}
                    >
                        Reconnect
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * One camera on a wall. Tapping it selects it (the timeline, snapshot
 * and talk-down act on the selected camera); the corner button, or a
 * double tap, opens it full screen.
 *
 * The tile is a container for CSS container queries, so the overlay
 * sizes itself to the tile rather than the window: the same component
 * is a thumbnail in a phone's two-column wall and a hero on a lobby
 * display.
 */
export const CameraTile = memo(forwardRef(function CameraTile({
    camera,
    index = 0,
    selected = false,
    onSelect,
    onExpand,
    label,
    className = '',
    ...view
}, feedRef) {
    const reduced = useReducedMotion();
    const classes = ['cam-tile'];
    if (selected) classes.push('cam-tile--selected');
    if (view.status && view.status !== 'live') classes.push('cam-tile--down');
    if (view.motion) classes.push('cam-tile--motion');
    if (className) classes.push(className);

    return (
        <motion.div
            className={classes.join(' ')}
            initial={reduced ? false : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: Math.min(index * 0.035, 0.3), ease: EASE }}
        >
            <button
                type="button"
                className="cam-tile__hit"
                aria-label={label || `${camera.name}, ${camera.location}`}
                aria-pressed={onSelect ? selected : undefined}
                onClick={() => (onSelect || onExpand)?.(camera.id)}
                onDoubleClick={() => onExpand?.(camera.id)}
            />
            <CameraView ref={feedRef} camera={camera} {...view} />
            {onExpand && (
                <button
                    type="button"
                    className="cam-tile__expand"
                    aria-label={`Open ${camera.name} full screen`}
                    onClick={event => { event.stopPropagation(); onExpand(camera.id); }}
                >
                    <FullscreenIcon aria-hidden="true" />
                </button>
            )}
        </motion.div>
    );
}));
